import React from "react";
import PageShell from "../components/layout/PageShell";
import CinematicHero, { HeroOverlay } from "../components/layout/CinematicHero";
import Container from "../components/primitives/Container";
import Section from "../components/primitives/Section";
import Eyebrow from "../components/primitives/Eyebrow";
import Reveal from "../components/primitives/Reveal";
import ParallaxImage from "../components/editorial/ParallaxImage";
import LivingImage from "../components/editorial/LivingImage";
import SeoHead from "../components/seo/SeoHead";
import { IMAGES } from "../content/images";
import { STRATEGY } from "../content/site";

const SectionDisplay = ({ children, className = "", tone = "light" }) => (
  <h2
    className={`font-serif font-normal text-balance ${
      tone === "dark" ? "text-offwhite" : "text-navy"
    } ${className}`}
    style={{ fontSize: "clamp(36px, 5vw, 72px)", lineHeight: 1.08, letterSpacing: "-0.01em" }}
  >
    {children}
  </h2>
);

const InvestmentStrategy = () => {
  return (
    <PageShell>
      <SeoHead
        title="Investment Strategy"
        description="A disciplined, operationally-led approach to long-duration capital across private equity, industrials, infrastructure, energy, and manufacturing."
        path="/investment-strategy"
        ogImage={IMAGES.strategy_hero_lng_arrival}
      />
      <CinematicHero
        image={IMAGES.strategy_hero_lng_arrival}
        imageAlt="An LNG carrier arriving at terminal at blue hour."
        eager
        data-testid="strategy-hero"
      >
        <HeroOverlay
          eyebrow={STRATEGY.hero.eyebrow}
          title={STRATEGY.hero.headline}
          statement={STRATEGY.overview.body}
        />
      </CinematicHero>

      {/* Overview — single sentence */}
      <Section tone="light" className="pt-20 md:pt-24 lg:pt-28 pb-6 md:pb-8 lg:pb-8" data-testid="strategy-overview">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
            <Reveal className="lg:col-span-6">
              <Eyebrow tone="gold">Overview</Eyebrow>
              <SectionDisplay className="mt-8">{STRATEGY.overview.heading}</SectionDisplay>
            </Reveal>
            <Reveal className="lg:col-span-6 lg:pt-4" delay={120}>
              <span aria-hidden="true" className="block h-px w-12 bg-gold" />
              <p className="mt-8 font-sans text-[17px] sm:text-[18px] leading-[1.75] text-navy/80 max-w-prose">
                {STRATEGY.overview.body}
              </p>
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* Target Areas */}
      <Section tone="offwhite" className="pt-6 md:pt-8 lg:pt-8 pb-6 md:pb-8 lg:pb-8" data-testid="strategy-target-areas">
        <Container>
          <Reveal className="max-w-2xl">
            <Eyebrow tone="gold">{STRATEGY.targetAreas.eyebrow}</Eyebrow>
            <SectionDisplay className="mt-8">{STRATEGY.targetAreas.heading}</SectionDisplay>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-16 lg:gap-x-24 gap-y-16">
            {STRATEGY.targetAreas.items.map((item, i) => {
              const targetImages = [
                IMAGES.strategy_pe_facade,
                IMAGES.strategy_industrial_terminal,
                IMAGES.strategy_bridge,
                IMAGES.strategy_refinery,
                IMAGES.strategy_battery_separator,
                IMAGES.strategy_command_center,
                IMAGES.strategy_port_elevated,
                IMAGES.strategy_family_boardroom,
              ];
              const targetAlt = [
                "Institutional headquarters facade at blue hour",
                "Automated container terminal at blue hour",
                "Cable-stayed bridge at blue hour",
                "Refinery at blue hour with cracking towers silhouetted",
                "Battery separator production line at blue hour",
                "Industrial operations command centre at blue hour",
                "Elevated international cargo port at blue hour",
                "Private walnut boardroom at blue hour with one warm reading lamp",
              ];
              const num = String(i + 1).padStart(2, "0");
              return (
                <Reveal key={item.name} delay={i * 90} variant="image">
                  <div data-testid={`strategy-target-card-${i + 1}`}>
                    <div className="aspect-[4/3] w-full bg-navy/5 relative">
                      <LivingImage
                        src={targetImages[i]}
                        alt={targetAlt[i]}
                        className="absolute inset-0"
                        variant="editorial"
                        imgStyle={{ filter: "saturate(0.9) brightness(0.96)" }}
                      />
                    </div>
                    <span
                      aria-hidden="true"
                      className="block h-px bg-gold mt-8"
                      style={{ width: 24 }}
                    />
                    <p
                      className="mt-4 font-sans uppercase text-gold"
                      style={{ fontSize: "11px", letterSpacing: "0.22em", fontWeight: 500 }}
                    >
                      {num}
                    </p>
                    <p
                      className="mt-2 font-serif text-navy"
                      style={{ fontSize: "clamp(22px, 2vw, 30px)", lineHeight: 1.2 }}
                    >
                      {item.name}
                    </p>
                    <p className="mt-4 font-sans text-[15px] leading-[1.7] text-navy/70 max-w-[52ch]">
                      {item.body}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </Section>

      {/* Characteristics — dark, names only with gold bullet */}
      <section
        data-testid="strategy-characteristics"
        className="bg-[var(--q-bg-dark)] text-offwhite pt-6 md:pt-8 lg:pt-8 pb-6 md:pb-8 lg:pb-8"
       data-section-tone="dark">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-center">
            <Reveal className="lg:col-span-6 lg:order-2">
              <ParallaxImage
                src={IMAGES.strategy_semi_corridor}
                alt="A semiconductor fabrication corridor at blue hour"
                className="aspect-[4/5] w-full"
                overlayStyle={{
                  background:
                    "linear-gradient(180deg, rgba(11,19,43,0.15) 0%, rgba(11,19,43,0.55) 100%)",
                }}
              />
            </Reveal>

            <Reveal className="lg:col-span-6 lg:order-1" delay={120}>
              <Eyebrow tone="gold">{STRATEGY.characteristics.eyebrow}</Eyebrow>
              <SectionDisplay tone="dark" className="mt-8">{STRATEGY.characteristics.heading}</SectionDisplay>

              <ul className="mt-14 space-y-7">
                {STRATEGY.characteristics.items.map((name) => (
                  <li key={name} className="flex items-center gap-5">
                    <span
                      aria-hidden="true"
                      className="inline-block bg-gold shrink-0"
                      style={{ width: 4, height: 4 }}
                    />
                    <p
                      className="font-serif text-offwhite"
                      style={{ fontSize: "clamp(22px, 2.4vw, 32px)", lineHeight: 1.3 }}
                    >
                      {name}
                    </p>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Energy split-block — Split B narrative interlude between Characteristics and the pull quote */}
      <section data-testid="strategy-energy-narrative" className="bg-offwhite">
        <Container>
          <div className="pt-6 md:pt-8 lg:pt-8 pb-20 md:pb-24 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-center">
            <Reveal className="lg:col-span-6" variant="image">
              <ParallaxImage
                src={IMAGES.strategy_lng_terminal}
                alt="A coastal LNG terminal at blue hour"
                className="aspect-[4/5] w-full"
                overlayStyle={{
                  background:
                    "linear-gradient(180deg, rgba(11,19,43,0.12) 0%, rgba(11,19,43,0.45) 100%)",
                }}
              />
            </Reveal>
            <Reveal className="lg:col-span-6 lg:pl-4" delay={120}>
              <Eyebrow tone="gold">Energy narrative</Eyebrow>
              <SectionDisplay className="mt-8">
                Assets that carry the century.
              </SectionDisplay>
              <span aria-hidden="true" className="mt-10 inline-block h-px w-12 bg-gold" />
              <p className="mt-8 font-sans text-[17px] sm:text-[18px] leading-[1.75] text-navy/80 max-w-prose">
                Our energy work is anchored in physical infrastructure — terminals, pipelines, offshore
                platforms — where economics compound through cycles and where operational discipline,
                not fashion, determines returns.
              </p>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Full-bleed pull quote — pylons background with subtle scroll parallax */}
      <section data-testid="strategy-interlude" className="relative">
        <ParallaxImage
          src={IMAGES.strategy_quote_pylons}
          alt="Transmission pylons at blue hour"
          className="aspect-[16/7] sm:aspect-[16/6] lg:aspect-[16/5] w-full"
          range={-20}
          overlayStyle={{
            background:
              "linear-gradient(180deg, rgba(11,19,43,0.55) 0%, rgba(11,19,43,0.35) 50%, rgba(11,19,43,0.6) 100%)",
          }}
        />
        <div className="absolute inset-0 flex items-center">
          <Container>
            <Reveal>
              <blockquote className="text-center max-w-3xl mx-auto">
                <p
                  className="font-serif italic text-offwhite text-balance"
                  style={{ fontSize: "clamp(28px, 3.4vw, 44px)", lineHeight: 1.2 }}
                >
                  &ldquo;{STRATEGY.interlude.quote}&rdquo;
                </p>
                <span aria-hidden="true" className="mt-10 inline-block h-px w-12 bg-gold" />
              </blockquote>
            </Reveal>
          </Container>
        </div>
      </section>
    </PageShell>
  );
};

export default InvestmentStrategy;
